import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FileSearch, Search, Scale, DollarSign, Headphones, Shield } from 'lucide-react';

const Services = () => {
  const { t, direction } = useLanguage();

  const icons = {
    FileSearch,
    Search,
    Scale,
    DollarSign,
    HeadphonesIcon: Headphones,
    Shield
  };

  const gradients = [
    'from-blue-500 to-blue-600',
    'from-purple-500 to-purple-600',
    'from-emerald-500 to-emerald-600',
    'from-amber-500 to-amber-600',
    'from-pink-500 to-pink-600',
    'from-indigo-500 to-indigo-600'
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#C9A55A]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#0A1628]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        
        {/* Section Header */}
        <div className={`text-center mb-16 ${direction === 'rtl' ? 'rtl' : ''}`}>
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-[#C9A55A]/10 border border-[#C9A55A]/30 rounded-full mb-6">
            <Scale className="w-4 h-4 text-[#C9A55A]" />
            <span className="text-sm font-semibold text-[#0A1628] uppercase tracking-wider">
              {direction === 'rtl' ? 'خدماتنا القانونية' : 'Our Legal Services'}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t.services.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent mx-auto mb-4" />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.services.list.map((service, idx) => {
            const IconComponent = icons[service.icon] || FileSearch;
            return (
              <div
                key={idx}
                className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 overflow-hidden"
                data-testid={`service-card-${idx}`}
              >
                {/* Hover Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] to-[#1a2942] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${gradients[idx]} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  
                  {/* Title */}
                  <h3 className={`text-xl font-bold text-[#0A1628] group-hover:text-white mb-3 transition-colors duration-300 ${direction === 'rtl' ? 'text-right' : ''}`}>
                    {service.title}
                  </h3>
                  
                  {/* Description */}
                  <p className={`text-gray-600 group-hover:text-gray-300 leading-relaxed transition-colors duration-300 ${direction === 'rtl' ? 'text-right' : ''}`}>
                    {service.description}
                  </p>
                  
                  {/* Decorative Line */}
                  <div className="w-12 h-1 bg-[#C9A55A] mt-6 rounded-full group-hover:w-full transition-all duration-500" />
                </div>
                
                {/* Corner Decoration */}
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-[#C9A55A]/5 rounded-full group-hover:bg-[#C9A55A]/10 transition-colors duration-300" />
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="px-10 py-4 bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628] rounded-lg font-bold text-lg shadow-xl hover:shadow-[0_0_30px_rgba(201,165,90,0.4)] transition-all duration-300 hover:scale-105"
            data-testid="services-cta"
          >
            {t.hero.cta}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
